import styled from "styled-components";
import {
  color_grey_ultra_dark,
  color_red_default,
  color_white,
} from "../../constants/colors";
import {
  getAuthHeroImageCss,
} from "./authHeroImage";
import { AUTH_MEDIA } from "./authLayoutBreakpoints";

export const AuthPage = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f7f6f4;
  color: ${color_grey_ultra_dark};

  @media ${AUTH_MEDIA.tablet} {
    justify-content: safe center;
    padding: 28px 0;
    box-sizing: border-box;
  }
`;

export const AuthHeader = styled.header`
  width: min(1360px, calc(100% - 56px));
  min-height: 76px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  @media ${AUTH_MEDIA.tablet} {
    width: min(${(props) => (props.$variant === "register" ? "640px" : "480px")}, calc(100% - 48px));
  }

  @media ${AUTH_MEDIA.mobile} {
    width: calc(100% - 32px);
    min-height: 64px;
  }
`;

export const AuthLogoButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
`;

export const AuthHelpButton = styled.button`
  border: 0;
  padding: 8px 0;
  background: transparent;
  color: ${color_red_default};
  font: 400 16px/1.2 Onest, sans-serif;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid rgba(240, 79, 79, 0.3);
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

export const AuthHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media ${AUTH_MEDIA.mobile} {
    gap: 14px;
  }

  @media (max-width: 380px) {
    gap: 10px;
  }
`;

export const AuthMain = styled.main`
  width: min(1360px, calc(100% - 56px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${(props) =>
    props.$variant === "register"
      ? "minmax(0, 0.92fr) minmax(620px, 1.08fr)"
      : "minmax(0, 1.18fr) minmax(430px, 0.82fr)"};
  min-height: ${(props) => (props.$variant === "register" ? "700px" : "680px")};
  overflow: hidden;
  border-radius: 32px;
  background: ${color_white};
  box-shadow: 0 18px 48px rgba(54, 54, 54, 0.08);

  @media ${AUTH_MEDIA.belowDesktop} {
    width: min(${(props) => (props.$variant === "register" ? "640px" : "480px")}, calc(100% - 48px));
    min-height: 0;
    flex: 0 0 auto;
    margin: 8px auto;
    grid-template-columns: 1fr;
    overflow: visible;
  }

  @media ${AUTH_MEDIA.mobile} {
    width: 100%;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
  }
`;

export const AuthHero = styled.div`
  position: relative;
  min-width: 0;
  min-height: 100%;
  overflow: hidden;
  background: #fef2e4;

  img {
    position: absolute;
    display: block;
    image-rendering: auto;
    pointer-events: none;
    ${(props) => getAuthHeroImageCss(props.$variant)}
  }

  @media ${AUTH_MEDIA.belowDesktop} {
    display: none;
  }
`;

export const AuthHeroCopy = styled.div`
  position: relative;
  z-index: 2;
  max-width: 460px;
  padding: 40px 40px 0;
  color: ${color_grey_ultra_dark};

  h2 {
    margin: 0;
    font: 700 36px/1.12 Onest, sans-serif;
    letter-spacing: -0.035em;
  }

  h2 span {
    color: ${color_red_default};
  }

  p {
    margin: 12px 0 0;
    max-width: 420px;
    color: #8a8a8a;
    font: 400 15px/1.45 Onest, sans-serif;
  }
`;

export const AuthHeroBrand = styled.span`
  span {
    color: ${color_red_default};
  }
`;

export const AuthHeroSubtitleTail = styled.span`
  white-space: nowrap;
`;

export const AuthContent = styled.section`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props.$variant === "register" ? "36px 44px" : "56px 56px")};
  box-sizing: border-box;
  background: ${color_white};

  @media ${AUTH_MEDIA.belowDesktop} {
    padding: 40px 36px 48px;
  }

  @media ${AUTH_MEDIA.mobile} {
    align-items: flex-start;
    padding: 24px 20px 36px;
    background: transparent;
  }
`;

export const AuthFooter = styled.footer`
  width: min(1360px, calc(100% - 56px));
  min-height: 78px;
  margin: auto auto 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-sizing: border-box;

  @media ${AUTH_MEDIA.belowDesktop} {
    width: min(${(props) => (props.$variant === "register" ? "640px" : "480px")}, calc(100% - 48px));
    min-height: 0;
    margin: 0 auto;
    padding: 20px 0 0;
    flex-direction: column;
    justify-content: center;
  }

  @media ${AUTH_MEDIA.mobile} {
    width: calc(100% - 32px);
    margin-top: auto;
    padding: 16px 0 max(20px, env(safe-area-inset-bottom));
    gap: 8px;
  }
`;

export const AuthFooterLinks = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 0;
  color: #9a9a9a;
  font: 400 13px/1.4 Onest, sans-serif;

  a,
  span {
    color: inherit;
    text-decoration: none;
    white-space: nowrap;
  }

  a:hover {
    color: ${color_red_default};
  }

  a + a::before {
    content: "";
    display: inline-block;
    width: 1px;
    height: 13px;
    margin: 0 18px;
    vertical-align: middle;
    background: #d6d6d6;
  }

  @media ${AUTH_MEDIA.belowDesktop} {
    justify-content: center;

    a + a::before {
      margin: 0 12px;
    }
  }

  @media ${AUTH_MEDIA.mobile} {
    justify-content: center;
    gap: 0;
    font-size: 12px;
    line-height: 1.3;

    a {
      padding: 6px 0;
    }

    a + a::before {
      display: inline-block;
      margin: 0 8px;
      height: 11px;
    }
  }
`;

export const AuthCopyright = styled.span`
  color: #9a9a9a;
  font: 400 13px/1.4 Onest, sans-serif;
  white-space: nowrap;

  @media ${AUTH_MEDIA.mobile} {
    font-size: 11px;
  }
`;
